import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPublicJson, validFeed, validArticles, readStored, writeStored, mergeArticleDetail, matchesArticle, reuseUnchangedArticles } from "../lib/data-client.mjs";

const FEED_CACHE = "neutral-last-good-feed-v1";
const EMPTY_STATS = { total_articles: 0, source_distribution: {} };

function getLocalArticles() {
  const feed = readStored(FEED_CACHE, null, validFeed);
  return feed?.articles || readStored("neutral-last-good-articles", [], validArticles);
}

export function useArticleAPI() {
  const [initialFeed] = useState(() => readStored(FEED_CACHE, null, validFeed));
  const [articles, setArticles] = useState(() => initialFeed?.articles || getLocalArticles());
  const [stats, setStats] = useState(initialFeed?.stats || EMPTY_STATS);
  const [loading, setLoading] = useState(articles.length === 0);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(initialFeed?.generated_at || null);
  const current = useRef({ articles, feed: initialFeed });
  const request = useRef(null);
  const mounted = useRef(true);
  const details = useRef(new Map());
  const controller = useRef(null);

  const fetchArticles = useCallback(async () => {
    if (request.current) return request.current;
    const operation = (async () => {
      controller.current = new AbortController();
      try {
        let feed;
        try {
          feed = await fetchPublicJson("feed.json", { signal: controller.current.signal });
          if (!validFeed(feed)) throw new Error("Invalid feed generation");
        } catch (error) {
          if (error.status !== 404 || current.current.feed) throw error;
          const legacy = await fetchPublicJson("articles.json", { signal: controller.current.signal });
          if (!validArticles(legacy) || (!legacy.length && current.current.articles.length)) throw new Error("Invalid legacy feed");
          if (mounted.current) { current.current.articles = legacy; setArticles(legacy); }
          return legacy;
        }
        if (current.current.feed && Date.parse(feed.generated_at) < Date.parse(current.current.feed.generated_at)) throw new Error("Older generation received");
        if (!feed.articles.length && current.current.articles.length) throw new Error("Unexpected empty feed");
        if (mounted.current) {
          const changed = feed.generated_at !== current.current.feed?.generated_at;
          if (changed) {
            const previousIds = new Set(current.current.articles.map((a) => a.id));
            const ids = new Set(feed.articles.filter((a) => !previousIds.has(a.id)).map((a) => a.id));
            const nextArticles = reuseUnchangedArticles(current.current.articles, feed.articles);
            current.current = { articles: nextArticles, feed };
            setArticles(nextArticles); setStats(feed.stats); setLastUpdated(feed.generated_at);
            writeStored(FEED_CACHE, feed);
            details.current.clear();
            if (previousIds.size && ids.size) window.dispatchEvent(new CustomEvent("new-articles", { detail: { ids } }));
          }
          setFetchError(Date.now() - Date.parse(feed.generated_at) > 15 * 60 * 1000 ? "Nyhederne er forsinkede. Vi prøver automatisk igen." : null);
        }
        return feed.articles;
      } catch {
        if (mounted.current) setFetchError(current.current.articles.length ? "Viser seneste hentede nyheder. Vi prøver automatisk igen." : "Nyheder kunne ikke hentes. Vi prøver automatisk igen.");
        return current.current.articles;
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    request.current = operation;
    try { return await operation; }
    finally { if (request.current === operation) request.current = null; }
  }, []);

  const fetchArticleDetail = useCallback(async (article) => {
    if (!article?.id || !article.has_detail) return article;
    const key = `${article.id}:${article.processed_at}`;
    if (details.current.has(key)) return mergeArticleDetail(article, details.current.get(key));
    try {
      const detail = await fetchPublicJson(article.detail_file || `article-details/${article.id}.json`);
      const merged = mergeArticleDetail(article, detail);
      if (merged !== article) {
        if (details.current.size >= 50) details.current.delete(details.current.keys().next().value);
        details.current.set(key, detail);
      }
      return merged;
    } catch { return article; }
  }, []);

  const searchArticles = useCallback(async (query) => query?.trim().length >= 2
    ? current.current.articles.filter((article) => matchesArticle(article, query)).slice(0, 20) : [], []);

  useEffect(() => {
    mounted.current = true;
    let stopped = false;
    let polling = false;
    let timer;
    const poll = async () => {
      if (polling || stopped) return;
      polling = true;
      try { if (!document.hidden) await fetchArticles(); }
      finally {
        polling = false;
        if (!stopped) timer = setTimeout(poll, 60000);
      }
    };
    const resume = () => { if (!document.hidden) { clearTimeout(timer); poll(); } };
    poll();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    return () => {
      stopped = true; mounted.current = false; clearTimeout(timer);
      controller.current?.abort();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("online", resume);
    };
  }, [fetchArticles]);

  return { articles, stats, loading, fetchArticles, fetchArticleDetail, searchArticles, fetchError, lastUpdated };
}
